(function qualucaBmiTrackingFactory(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.QualucaBmiTrackingCore = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function createTrackingCore() {
  'use strict';

  const COOKIE_DAYS = 90;
  const COOKIE_NAMES = Object.freeze({
    visitor: 'qcalc_analytics_id',
    session: 'qcalc_analytics_session',
    attribution: 'qcalc_attribution',
  });

  function randomToken(cryptoApi) {
    if (!cryptoApi || typeof cryptoApi.getRandomValues !== 'function') return '';
    const bytes = new Uint8Array(32);
    cryptoApi.getRandomValues(bytes);
    return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  }

  function isToken(value) {
    return /^[a-f0-9]{64}$/.test(String(value || ''));
  }

  function readCookie(cookieString, name) {
    const prefix = `${encodeURIComponent(name)}=`;
    const entry = String(cookieString || '').split(';').map((item) => item.trim()).find((item) => item.startsWith(prefix));
    return entry ? decodeURIComponent(entry.slice(prefix.length)) : '';
  }

  function cookieLine(name, value, maxAgeSeconds) {
    const age = Number.isFinite(maxAgeSeconds) ? `; Max-Age=${Math.max(0, Math.floor(maxAgeSeconds))}` : '';
    return `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Path=/; Secure; SameSite=Lax${age}`;
  }

  function deleteCookieLine(name) {
    return `${encodeURIComponent(name)}=; Path=/; Secure; SameSite=Lax; Max-Age=0`;
  }

  function analyticsAllowed(shopify) {
    try {
      return shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch (_) {
      return false;
    }
  }

  function deviceGroup(width) {
    if (!Number.isFinite(width)) return null;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  function languageCode(language) {
    const value = String(language || '').trim().slice(0, 16);
    return /^[a-z]{2,3}(?:-[A-Z]{2})?$/.test(value) ? value : null;
  }

  function campaignContext(search) {
    const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
    const clean = (name) => String(params.get(name) || '').trim().slice(0, 100) || null;
    const utmSource = clean('utm_source');
    const utmMedium = clean('utm_medium');
    const utmCampaign = clean('utm_campaign');
    return {
      sourceGroup: utmSource ? 'campaign' : 'direct_or_unknown',
      utmSource,
      utmMedium,
      utmCampaign,
    };
  }

  function transition(previousAllowed, nextAllowed) {
    return {
      becameAllowed: previousAllowed !== true && nextAllowed === true,
      withdrew: previousAllowed === true && nextAllowed !== true,
      retroLinkAllowed: false,
    };
  }

  function createPrivacyState(env) {
    const windowObject = env.window;
    const documentObject = env.document;
    let allowed = false;

    function setCookie(name, value, maxAge) {
      documentObject.cookie = cookieLine(name, value, maxAge);
    }

    function removeCookie(name) {
      documentObject.cookie = deleteCookieLine(name);
    }

    function clearAll() {
      Object.values(COOKIE_NAMES).forEach(removeCookie);
    }

    function ensureTokens() {
      if (!allowed) return {visitorToken: '', sessionToken: ''};
      let visitorToken = readCookie(documentObject.cookie, COOKIE_NAMES.visitor);
      let sessionToken = readCookie(documentObject.cookie, COOKIE_NAMES.session);
      if (!isToken(visitorToken)) {
        visitorToken = randomToken(windowObject.crypto);
        if (visitorToken) setCookie(COOKIE_NAMES.visitor, visitorToken, COOKIE_DAYS * 86400);
      }
      if (!isToken(sessionToken)) {
        sessionToken = randomToken(windowObject.crypto);
        if (sessionToken) setCookie(COOKIE_NAMES.session, sessionToken);
      }
      return {visitorToken, sessionToken};
    }

    function refresh() {
      const next = analyticsAllowed(windowObject.Shopify);
      const change = transition(allowed, next);
      allowed = next;
      if (!allowed) clearAll();
      else ensureTokens();
      return change;
    }

    function snapshot() {
      if (!allowed) return {trackingType: 'anonymous', analyticsAllowed: false};
      const tokens = ensureTokens();
      const campaign = campaignContext(windowObject.location?.search || '');
      return {
        trackingType: 'analytics', analyticsAllowed: true,
        analyticsVisitorToken: tokens.visitorToken,
        analyticsSessionToken: tokens.sessionToken,
        ...campaign,
        deviceGroup: deviceGroup(windowObject.innerWidth),
        languageCode: languageCode(windowObject.navigator?.language),
      };
    }

    function setAttribution(token) {
      if (allowed && isToken(token)) setCookie(COOKIE_NAMES.attribution, token, COOKIE_DAYS * 86400);
    }

    function currentAttribution() {
      if (!allowed) return '';
      const token = readCookie(documentObject.cookie, COOKIE_NAMES.attribution);
      return isToken(token) ? token : '';
    }

    refresh();
    return {refresh, snapshot, clearAll, setAttribution, currentAttribution, isAnalyticsAllowed: () => allowed};
  }

  function anonymousPayloadHasForbiddenKeys(payload) {
    const forbidden = ['analyticsVisitorToken','analyticsSessionToken','sourceGroup','utmSource','utmMedium','utmCampaign','deviceGroup','languageCode','customerId','email','orderId','ip','userAgent'];
    return forbidden.some((key) => payload && payload[key] != null);
  }

  return Object.freeze({
    COOKIE_DAYS, COOKIE_NAMES, randomToken, isToken, readCookie, cookieLine, deleteCookieLine,
    analyticsAllowed, deviceGroup, languageCode, campaignContext, transition, createPrivacyState,
    anonymousPayloadHasForbiddenKeys,
  });
});
